import React, {useMemo} from 'react';
import {paginateRows, sortRows} from "./tableHelpers";
import {onItemsListInfoButtonClick} from "../../helpers/ComponentHelper";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit, faTrashAlt, faSquare} from "@fortawesome/free-solid-svg-icons";
import useCrud from "../../hooks/useCrud";
import FormCheck from "react-bootstrap/FormCheck";

const TableBody = ({
  selectedItems,
  setSelectedItems,
  columns,
  sort,
  activePage,
  filteredRows,
  rowsPerPage,
  state,
  checkRelatedItems,
  formHeader,
  relatedItemsUrl,
  getCompleteItem
}) => {
  const sortedRows = useMemo(() => sortRows(filteredRows, sort, columns), [filteredRows, sort, columns]);
  const calculatedRows = paginateRows(sortedRows, activePage, rowsPerPage);
  const {getRelatedChildrenByParentId} = useCrud();
  const setCurrentItem = state.setCurrentItem;
  const setBackupItem = state.setBackupItem;
  const currentFormState = state.currentFormState;
  const setCurrentFormState = state.setCurrentFormState;
  const relatedItems = state.setRelatedItems;

  const parseColorsMap = s =>
    Object.fromEntries(
      s.split(";")
        .map(e => e.split("=").slice(0, 2).map(t => t.trim()))
        .filter(([k, v]) => k && v)
    );

  const parseContentMap = s =>
    Object.fromEntries(
      s
        .split(";")
        .map(e => e.split("=").slice(0, 2).map(t => t.trim().replace(/^['"]|['"]$/g, "")))
        .filter(([k, v]) => k && v)
    );

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const toRgb = colorValue => {
    if (!colorValue || typeof colorValue !== "string") return null;
    const color = colorValue.trim();
    const shortHex = /^#([0-9a-f]{3})$/i;
    const longHex = /^#([0-9a-f]{6})$/i;
    const rgb = /^rgba?\(([^)]+)\)$/i;

    if (shortHex.test(color)) {
      const [, hex] = color.match(shortHex);
      return {
        r: parseInt(hex[0] + hex[0], 16),
        g: parseInt(hex[1] + hex[1], 16),
        b: parseInt(hex[2] + hex[2], 16)
      };
    }

    if (longHex.test(color)) {
      const [, hex] = color.match(longHex);
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16)
      };
    }

    if (rgb.test(color)) {
      const parts = color.match(rgb)[1].split(",").map(part => Number(part.trim()));
      if (parts.length >= 3 && parts.every(part => !Number.isNaN(part))) {
        return {
          r: clamp(parts[0], 0, 255),
          g: clamp(parts[1], 0, 255),
          b: clamp(parts[2], 0, 255)
        };
      }
    }

    return null;
  };

  const rgba = (rgb, alpha) => rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})` : null;

  const getTagPalette = (accentColor, explicitTextColor) => {
    const rgb = toRgb(accentColor);
    return {
      accent: accentColor || "#6c757d",
      border: rgb ? rgba(rgb, 0.36) : "#d7dde3",
      background: rgb ? rgba(rgb, 0.12) : "#ffffff",
      text: explicitTextColor || "#34404a"
    };
  };

  const openDetails = async (item) => {
    relatedItemsUrl && getRelatedChildrenByParentId(`${relatedItemsUrl}/${item.id}`, item.id, relatedItems);
    if (getCompleteItem) {
      const completeItem = await getCompleteItem(item.id);
      setCurrentItem(completeItem);
      setBackupItem(completeItem);
    } else {
      setCurrentItem(item);
      setBackupItem(item);
    }
    onItemsListInfoButtonClick(currentFormState, setCurrentFormState, formHeader);
  };

  return (
    <tbody>
    {calculatedRows.map((item) => (
      <tr key={item.id} onDoubleClick={() => openDetails(item)}>
        <td>
          <FormCheck
            type="checkbox"
            checked={selectedItems.has(item.id)}
            onChange={() => {
              const newSelectedItems = new Set(selectedItems);
              if (selectedItems.has(item.id)) {
                newSelectedItems.delete(item.id);
              } else {
                newSelectedItems.add(item.id);
              }
              setSelectedItems(newSelectedItems);
            }}
          />
        </td>

        {columns.map((column) => {
          if (column.visible === false) {
            return null;
          }

          let content = item[column.accessor];
          let baseObject = null;
          let backgroundColor = null;
          let textColor = null;

          if (column.accessor.includes(".")) {
            const keys = column.accessor.split(".");
            content = keys.reduce((obj, key) => (obj && obj[key] !== undefined ? obj[key] : ""), item);
            baseObject = item[keys[0]];
          } else {
            baseObject = item[column.accessor];
          }

          if (column.format) {
            content = column.format(baseObject);
          }

          if (column.accessor.includes(".") || (column.fieldsToInclude && (column.fieldsToInclude.includes("name") || column.fieldsToInclude.includes("shortName")))) {
            if (baseObject && typeof baseObject === "object" && baseObject.color) {
              backgroundColor = baseObject.color;
              textColor = baseObject.fcolor;
            }
          }

          if (column.color) {
            if (column.color.includes(".")) {
              const keys = column.color.split(".");
              backgroundColor = keys.reduce((obj, key) => (obj && obj[key] !== undefined ? obj[key] : ""), item);
            } else {
              backgroundColor = "white";
            }
          }

          if (column.colorsMap) {
            const map = parseColorsMap(column.colorsMap);
            backgroundColor = map[content] ?? map.default ?? backgroundColor ?? "#6c757d";
          }

          if (column.fcolor) {
            const keys = column.fcolor.split(".");
            textColor = keys.reduce((obj, key) => (obj && obj[key] !== undefined ? obj[key] : ""), item);
          }

          if (column.accessor === "editBtn") {
            return (
              <td key={`${item.id}-${column.accessor}`}>
                <button className='btn btn-outline-info' onClick={() => openDetails(item)}><FontAwesomeIcon icon={faEdit}/></button>
              </td>
            );
          }

          if (column.accessor === "deleteBtn") {
            return (
              <td key={`${item.id}-${column.accessor}`}>
                <button className='btn btn-outline-danger' onClick={() => {
                  setCurrentItem(item);
                  checkRelatedItems(item.id);
                }}><FontAwesomeIcon icon={faTrashAlt}/></button>
              </td>
            );
          }

          if (column.type === "color") {
            return <td key={`${item.id}-${column.accessor}`}><FontAwesomeIcon style={{color: item.color}} icon={faSquare}/></td>;
          }

          if (column.type === "bool") {
            return (
              <td key={`${item.id}-${column.accessor}`}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  readOnly={true}
                  checked={!!content}
                />
              </td>
            );
          }

          if (content === "" || content === undefined || content === null) {
            return <td key={`${item.id}-${column.accessor}`}></td>;
          }

          let displayContent = content;
          if (column.contentMap) {
            const cmap = parseContentMap(column.contentMap);
            displayContent = cmap[String(content)] ?? cmap.default ?? content;
          }

          const shouldRenderTag = !!(column.accessor.includes(".") || column.fieldsToInclude || column.contentMap || column.colorsMap);
          const palette = getTagPalette(backgroundColor, textColor);

          return (
            <td key={`${item.id}-${column.accessor}`}>
              <span
                className={shouldRenderTag ? "accessor_object" : ""}
                style={shouldRenderTag ? {
                  "--tag-accent": palette.accent,
                  "--tag-border": palette.border,
                  "--tag-bg": palette.background,
                  "--tag-text": palette.text
                } : {}}
              >
                {displayContent}
              </span>
            </td>
          );
        })}
      </tr>
    ))}
    </tbody>
  );
};

export default TableBody;
